// Pure formatting helpers for gamification v2 surfaces. No React, no I/O.

const NBSP = " ";
const MINUS = "−";

// 1,200 · 12.4K · 1.2M
export function formatCount(n) {
  const value = Number(n);
  if (!Number.isFinite(value)) return "0";
  const abs = Math.abs(value);
  if (abs < 10000) return Math.round(value).toLocaleString("en-US");
  const short = (divisor, suffix) => {
    const text = (value / divisor).toFixed(1).replace(/\.0$/, "");
    return `${text}${suffix}`;
  };
  if (abs < 1e6) return short(1e3, "K");
  return short(1e6, "M");
}

// Rates arrive as either 0–1 fractions or 0–100 percents; normalise to 0–100.
export function asPercent(x) {
  const value = Number(x);
  if (x === null || x === undefined || !Number.isFinite(value)) return null;
  return value <= 1 ? value * 100 : value;
}

// "80 %" — non-breaking space so the unit never wraps away from the number.
export function formatPercent(x, digits = 0) {
  const pct = asPercent(x);
  if (pct === null) return "—";
  return `${pct.toFixed(digits)}${NBSP}%`;
}

// Relative change as "+12 %", "−3 %" or "±0". Pass a delta (percent) directly,
// or current + previous to compute one.
export function formatDelta(current, previous) {
  let delta;
  if (previous === undefined) {
    delta = Number(current);
  } else {
    const prev = Number(previous);
    const curr = Number(current);
    if (!Number.isFinite(prev) || !Number.isFinite(curr)) return "—";
    if (prev === 0) delta = curr === 0 ? 0 : 100;
    else delta = ((curr - prev) / Math.abs(prev)) * 100;
  }
  if (!Number.isFinite(delta)) return "—";
  const rounded = Math.round(delta);
  if (rounded === 0) return "±0";
  const sign = rounded > 0 ? "+" : MINUS;
  return `${sign}${Math.abs(rounded)}${NBSP}%`;
}

export function deltaSign(delta) {
  const value = Number(delta);
  if (!Number.isFinite(value) || Math.round(value) === 0) return 0;
  return value > 0 ? 1 : -1;
}

// 45s · 4m 12s · 1h 03m
export function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (total < 60) return `${total}s`;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

// "Sep 14" — short, locale-stable date for lists.
export function formatShortDate(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(date);
}

// Whole days until a timestamp (0 when already past). Used for streak expiry.
export function daysUntil(value, now = Date.now()) {
  if (!value) return null;
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return null;
  return Math.max(0, Math.ceil((target - now) / 86400000));
}

// Human label from a camelCase key: "top10Supporter" → "Top 10 supporter".
export function humanizeKey(key) {
  if (!key) return "";
  return String(key)
    .replace(/([a-z])([A-Z0-9])/g, "$1 $2")
    .replace(/([0-9])([A-Za-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());
}
